/**
 * Header carrying the Cloudflare Turnstile token from the browser through the
 * BFF to the Hub (AP-11097, AP-11102).
 */
export const CAPTCHA_TOKEN_HEADER = 'cf-turnstile-response'

// The Hub's 400 `error` values for a captcha rejection. Used to detect the
// state only; the text shown to visitors lives in the frontend copy.
const HUB_CAPTCHA_ERRORS = new Set([
  'Missing captcha token.',
  'Invalid captcha token.',
  'Expired captcha token.',
  'Captcha verification failed. Please try again.',
])

const hubErrorOf = (value: unknown): unknown =>
  (value as { error?: unknown } | null | undefined)?.error

/**
 * Whether a failed request was rejected by the Hub's captcha check. Accepts
 * either the upstream body (`{ error }`) or a BFF error, which nests it under
 * `data` (`{ data: { error } }`) or `data.data` on the client.
 */
export const isCaptchaRejection = (
  status: number | undefined,
  body: unknown
) => {
  if (status !== 400) return false

  const nested = (body as { data?: unknown } | null | undefined)?.data
  const candidates = [
    hubErrorOf(body),
    hubErrorOf(nested),
    hubErrorOf((nested as { data?: unknown } | null | undefined)?.data),
  ]

  return candidates.some(
    (candidate) =>
      typeof candidate === 'string' && HUB_CAPTCHA_ERRORS.has(candidate)
  )
}

/**
 * Header carrying how the browser's token request went, from the browser to
 * the BFF only (AP-11209). The BFF logs it and never forwards it to the Hub.
 */
export const CAPTCHA_OUTCOME_HEADER = 'x-captcha-outcome'

/**
 * How a token request ended. `absent` is the BFF's own value for a request
 * that carried no outcome header at all, which no browser of ours sends.
 */
export const CAPTCHA_OUTCOMES = [
  'token',
  'timeout',
  'interactive_timeout',
  'error',
  'expired',
  'script_blocked',
  'not_mounted',
  'not_configured',
  'cancelled',
] as const

export type TCaptchaOutcome = (typeof CAPTCHA_OUTCOMES)[number]

export interface ICaptchaResult {
  token: string | null
  outcome: TCaptchaOutcome
  // Cloudflare's numeric error code, only with `outcome: 'error'`.
  errorCode?: string
  // Cloudflare asked the visitor to interact before issuing a token.
  interactive: boolean
  durationMs: number
}

export interface ICaptchaOutcomeLog {
  outcome: TCaptchaOutcome | 'absent'
  error_code: string | null
  interactive: boolean | null
  duration_ms: number | null
}

const ERROR_CODE_PATTERN = /^[0-9a-z]{1,16}$/i
const MAX_DURATION_MS = 600_000

const isCaptchaOutcome = (value: string | null): value is TCaptchaOutcome =>
  CAPTCHA_OUTCOMES.includes(value as TCaptchaOutcome)

/**
 * Encodes a result for the outcome header. The token itself never goes in it.
 */
export const encodeCaptchaOutcome = (result: ICaptchaResult): string => {
  const params = new URLSearchParams({
    outcome: result.outcome,
    interactive: result.interactive ? '1' : '0',
    ms: String(Math.round(result.durationMs)),
  })

  if (result.errorCode) params.set('code', result.errorCode)

  return params.toString()
}

/**
 * Reads the outcome header on the BFF. Anything outside the known values is
 * dropped, so a client can't put arbitrary text into our logs.
 */
export const parseCaptchaOutcome = (
  header: string | null | undefined
): ICaptchaOutcomeLog => {
  const params = new URLSearchParams(header ?? '')
  const outcome = params.get('outcome')

  if (!isCaptchaOutcome(outcome)) {
    return {
      outcome: 'absent',
      error_code: null,
      interactive: null,
      duration_ms: null,
    }
  }

  const code = params.get('code')
  const ms = Number(params.get('ms'))

  return {
    outcome,
    error_code: code && ERROR_CODE_PATTERN.test(code) ? code : null,
    interactive: params.get('interactive') === '1',
    duration_ms:
      Number.isFinite(ms) && ms >= 0
        ? Math.min(Math.round(ms), MAX_DURATION_MS)
        : null,
  }
}

/**
 * Headers for an outgoing request. From the browser, pass the token request's
 * result: the token goes under the Hub header and the outcome next to it. From
 * the BFF to the Hub, pass the token alone. No token means no token header, so
 * the Hub decides per endpoint.
 */
export const captchaHeaders = (
  source: ICaptchaResult | string | null | undefined
): Record<string, string> => {
  if (!source) return {}
  if (typeof source === 'string') return { [CAPTCHA_TOKEN_HEADER]: source }

  return {
    ...(source.token ? { [CAPTCHA_TOKEN_HEADER]: source.token } : {}),
    [CAPTCHA_OUTCOME_HEADER]: encodeCaptchaOutcome(source),
  }
}
